import { MAX_PRECISION_ROUND_DIGITS } from '@onestep/camel-api';

import type { ParameterConfig } from '@/types/measurements';

import { transformNormData } from '../../utils/parameterNormsUtil';
import type { INormV2 } from './types';
import { paramKeyMapping } from './types';

const applyPrecisionOverrides = (metadata: ParameterConfig[]): ParameterConfig[] => {
  const isShowMaxPrecision = localStorage.getItem('isShowMaxPrecision') === 'true';

  return metadata.map((param) => ({
    ...param,
    roundDigits: (isShowMaxPrecision && MAX_PRECISION_ROUND_DIGITS[param.key]) || param.roundDigits,
  }));
};

const transformNorms = ({
  norms,
  metadata,
  isImperial,
}: {
  norms: INormV2[];
  metadata: ParameterConfig[];
  isImperial: boolean;
}) =>
  norms.map((norm) => ({
    ...norm,
    norm: transformNormData(metadata, norm.norm, norm.key, isImperial),
  }));

export const transformNormsResult = (
  data:
    | {
        metadata: ParameterConfig[];
        fraNorms: INormV2[];
        specificNorms: INormV2[];
      }
    | undefined,
  isImperial: boolean,
) => {
  if (!data) return { metadata: [], specificNorms: [], fraNorms: [] };
  const transformedMetadata = applyPrecisionOverrides(data.metadata);
  // FIX: Resolves stance asymmetry issue in old Walks API responses due to missing rounding digits.
  // Related Ticket: https://onesteprnd.atlassian.net/browse/OS-10756
  const additionalMetadata = transformedMetadata
    .filter(({ key }) => paramKeyMapping[key])
    .map((param) => ({
      ...param,
      key: paramKeyMapping[param.key],
    }));
  const updatedMetadata: ParameterConfig[] = [...transformedMetadata, ...additionalMetadata];

  return {
    metadata: updatedMetadata,
    specificNorms: transformNorms({
      norms: [
        ...data.specificNorms,
        // FIX: Resolves stance asymmetry issue in old Walks API responses due to missing norms.
        // Related Ticket: https://onesteprnd.atlassian.net/browse/OS-10526
        ...data.specificNorms
          .filter(({ key }) => paramKeyMapping[key])
          .map((norms) => ({
            ...norms,
            key: paramKeyMapping[norms.key],
          })),
      ],
      metadata: updatedMetadata,
      isImperial,
    }),
    fraNorms: transformNorms({
      norms: [
        ...data.fraNorms,
        // FIX: Resolves stance asymmetry issue in old Walks API responses due to missing norms.
        // Related Ticket: https://onesteprnd.atlassian.net/browse/OS-10526
        ...data.fraNorms
          .filter(({ key }) => paramKeyMapping[key])
          .map((norms) => ({
            ...norms,
            key: paramKeyMapping[norms.key],
          })),
      ],
      metadata: updatedMetadata,
      isImperial,
    }),
  };
};
